//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Popover, {
  Props as PopoverProps,
} from '@components/molecules/popover/Popover';

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactElement;
  sheetContent: React.ReactElement | React.ReactNode;
  onToggleDetailsSheet?: (isOpen: boolean) => void;
}

type Props = IOwnProps &
  Omit<PopoverProps, 'shouldShowArrow' | 'target' | 'content'>;

/**
 * The DetailsSheet molecule renders a popover with standard
 * styles (padding, width, etc) which is often
 * used to display information to the user in contexts in
 * which we display a summary by default (e.g. showing the
 * entire caption for a social post rather than just a
 * shortened version).
 *
 * Any children in this component act as the target element
 * and will be used to display the sheet on hover.
 */
export const DetailsSheet = (
  {
    className,
    children,
    sheetContent,
    onToggleDetailsSheet = () => undefined,
    ...popoverProps
  }: Props
) => {
  const contents = (
    <ContentsWrapper className={className}>{sheetContent}</ContentsWrapper>
  );

  return (
    <Popover
      content={contents}
      onTogglePopover={onToggleDetailsSheet}
      shouldShowArrow={false}
      target={children}
      {...popoverProps}
    />
  );
};

const ContentsWrapper = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[3]};
  width: ${theme.smallMeasure};
  max-height: ${theme.smallMeasure};
  overflow-y: auto;
  word-break: break-word;
`,
);

export default DetailsSheet;
